import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, Save, Sparkles, Dumbbell, Ruler, Sun } from 'lucide-react';
import { API } from '@/lib/api';



export default function GlowModule() {
  const { language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [saving, setSaving] = useState(false);
  const [glowScore, setGlowScore] = useState(null);
  const [log, setLog] = useState({
    date: today, skin_score: 50, waist_cm: '', upper_arm_cm: '', hip_cm: '',
    training_done: false, training_type: '', training_minutes: 0, skincare_routine: [], notes: ''
  });

  const de = language === 'de';
  const trainingTypes = [
    { val: 'core', label: de ? '15 Min Core' : '15 Min Core' },
    { val: 'glow_yoga', label: de ? 'Glow-Yoga' : 'Glow Yoga' },
    { val: 'walk', label: de ? 'Power-Walk' : 'Power Walk' },
    { val: 'strength', label: de ? 'Krafttraining' : 'Strength' },
    { val: 'other', label: de ? 'Andere' : 'Other' },
  ];
  const skincareOpts = de
    ? ['Reinigung', 'Vitamin C Serum', 'Hyaluron', 'Retinol', 'Sonnenschutz', 'Nachtpflege', 'Augenpflege', 'Peeling']
    : ['Cleansing', 'Vitamin C Serum', 'Hyaluronic Acid', 'Retinol', 'Sunscreen', 'Night Cream', 'Eye Cream', 'Exfoliant'];

  const fetchData = useCallback(async () => {
    try {
      const h = getHeaders();
      const [logRes, scoreRes] = await Promise.all([
        axios.get(`${API}/modules/glow/log/${today}`, { headers: h }),
        axios.get(`${API}/modules/glow-score`, { headers: h }),
      ]);
      if (logRes.data?.skin_score) setLog(prev => ({ ...prev, ...logRes.data, waist_cm: logRes.data.waist_cm || '', upper_arm_cm: logRes.data.upper_arm_cm || '', hip_cm: logRes.data.hip_cm || '' }));
      setGlowScore(scoreRes.data);
    } catch (e) { console.error(e); }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/modules/glow/log`, {
        ...log,
        waist_cm: log.waist_cm ? parseFloat(log.waist_cm) : null,
        upper_arm_cm: log.upper_arm_cm ? parseFloat(log.upper_arm_cm) : null,
        hip_cm: log.hip_cm ? parseFloat(log.hip_cm) : null,
      }, { headers: getHeaders() });
      toast.success(de ? 'Gespeichert!' : 'Saved!');
      const scoreRes = await axios.get(`${API}/modules/glow-score`, { headers: getHeaders() });
      setGlowScore(scoreRes.data);
    } catch { toast.error(de ? 'Fehler' : 'Error'); }
    setSaving(false);
  };

  const toggleSkincare = (s) => setLog(prev => ({
    ...prev, skincare_routine: prev.skincare_routine.includes(s) ? prev.skincare_routine.filter(x => x !== s) : [...prev.skincare_routine, s]
  }));

  const score = glowScore?.glow_score || 0;
  const bd = glowScore?.breakdown || {};

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl" data-testid="glow-module">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors" data-testid="glow-back"><ArrowLeft size={20} /></button>
        <div>
          <Badge className="bg-amber-100 text-amber-700 border-0 rounded-full font-body text-xs mb-1">{de ? 'Modul 5' : 'Module 5'}</Badge>
          <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Fit / Schön / Gesund' : 'Fit / Beautiful / Healthy'}</h1>
        </div>
      </div>

      {/* Glow Score Hero */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="glow-score-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="text-amber-500" size={24} />
            <h2 className="font-display text-2xl font-medium">{de ? 'Dein Glow Score' : 'Your Glow Score'}</h2>
          </div>
          <span className="font-display text-5xl font-bold text-amber-600">{score}<span className="text-xl font-normal text-amber-400">/100</span></span>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {[
            { key: 'skin', label: de ? 'Haut' : 'Skin', pct: 30 },
            { key: 'waist', label: de ? 'Taille' : 'Waist', pct: 20 },
            { key: 'energy', label: de ? 'Energie' : 'Energy', pct: 20 },
            { key: 'sleep', label: de ? 'Schlaf' : 'Sleep', pct: 20 },
            { key: 'training', label: 'Training', pct: 10 },
          ].map(f => (
            <div key={f.key} className="text-center">
              <p className="font-body text-[10px] text-foreground/50">{f.label} ({f.pct}%)</p>
              <Progress value={bd[f.key] || 0} className="h-1.5 bg-amber-100 mt-1 [&>div]:bg-amber-500" />
              <p className="font-body text-xs font-medium mt-0.5">{bd[f.key] || 0}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Skin Score */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="glow-skin-card">
        <div className="flex items-center gap-2 mb-3">
          <Sun size={20} className="text-amber-500" />
          <h2 className="font-display text-xl font-medium">{de ? 'Hautglow' : 'Skin Glow'}</h2>
        </div>
        <p className="font-body text-xs text-foreground/50 mb-3">{de ? 'Bewerte dein Hautbild heute (Selfie-Check)' : 'Rate your skin today (selfie check)'}</p>
        <div className="flex items-center gap-4">
          <Slider value={[log.skin_score]} onValueChange={([v]) => setLog({ ...log, skin_score: v })} min={0} max={100} step={1} className="flex-1 [&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-500" data-testid="glow-skin-slider" />
          <span className="font-display text-2xl font-semibold text-amber-600 min-w-[4ch]">{log.skin_score}</span>
        </div>
      </Card>

      {/* Body Measurements */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="glow-measurements-card">
        <div className="flex items-center gap-2 mb-4">
          <Ruler size={20} className="text-sage" />
          <h2 className="font-display text-xl font-medium">{de ? 'Körpermasse' : 'Body Measurements'}</h2>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="font-body text-xs text-foreground/60">{de ? 'Taille (cm)' : 'Waist (cm)'}</Label>
            <Input type="number" step="0.1" value={log.waist_cm} onChange={e => setLog({ ...log, waist_cm: e.target.value })} className="mt-1 rounded-xl" data-testid="glow-waist" placeholder="--" />
          </div>
          <div>
            <Label className="font-body text-xs text-foreground/60">{de ? 'Oberarm (cm)' : 'Upper Arm (cm)'}</Label>
            <Input type="number" step="0.1" value={log.upper_arm_cm} onChange={e => setLog({ ...log, upper_arm_cm: e.target.value })} className="mt-1 rounded-xl" data-testid="glow-arm" placeholder="--" />
          </div>
          <div>
            <Label className="font-body text-xs text-foreground/60">{de ? 'Po (cm)' : 'Hip (cm)'}</Label>
            <Input type="number" step="0.1" value={log.hip_cm} onChange={e => setLog({ ...log, hip_cm: e.target.value })} className="mt-1 rounded-xl" data-testid="glow-hip" placeholder="--" />
          </div>
        </div>
      </Card>

      {/* Training */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="glow-training-card">
        <div className="flex items-center gap-2 mb-4">
          <Dumbbell size={20} className="text-terracotta" />
          <h2 className="font-display text-xl font-medium">Training</h2>
        </div>
        <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="glow-training-check">
          <Checkbox checked={log.training_done} onCheckedChange={checked => setLog({ ...log, training_done: checked })} className="data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta" />
          <span className="font-body text-sm font-medium">{de ? 'Heute trainiert' : 'Trained today'}</span>
        </label>
        {log.training_done && (
          <div className="space-y-3 animate-fade-in">
            <div className="flex flex-wrap gap-2">
              {trainingTypes.map(t => (
                <button key={t.val} onClick={() => setLog({ ...log, training_type: t.val })} className={`px-3 py-1.5 rounded-full font-body text-sm border transition-all ${log.training_type === t.val ? 'bg-terracotta text-white border-terracotta' : 'bg-white text-foreground/60 border-border/60'}`} data-testid={`training-${t.val}`}>{t.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Label className="font-body text-sm text-foreground/60 shrink-0">{de ? 'Minuten:' : 'Minutes:'}</Label>
              <Input type="number" value={log.training_minutes} onChange={e => setLog({ ...log, training_minutes: parseInt(e.target.value) || 0 })} className="w-24 rounded-xl" data-testid="glow-training-mins" />
            </div>
          </div>
        )}
      </Card>

      {/* Skincare Routine */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="glow-skincare-card">
        <h2 className="font-display text-xl font-medium mb-3">{de ? 'Pflege-Routine' : 'Skincare Routine'}</h2>
        <div className="flex flex-wrap gap-2">
          {skincareOpts.map(s => (
            <button key={s} onClick={() => toggleSkincare(s)} className={`px-3 py-1.5 rounded-full font-body text-sm transition-all border ${log.skincare_routine.includes(s) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-foreground/60 border-border/60 hover:border-amber-300'}`} data-testid={`skincare-${s}`}>{s}</button>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6">
        <Label className="font-body text-sm font-medium text-foreground/70">{de ? 'Notizen' : 'Notes'}</Label>
        <Textarea value={log.notes} onChange={e => setLog({ ...log, notes: e.target.value })} className="mt-2 rounded-xl" rows={2} data-testid="glow-notes" />
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto rounded-full bg-amber-500 hover:bg-amber-600 text-white px-10 py-5 text-base font-medium shadow-lg shadow-amber-500/20" data-testid="glow-save-btn">
        <Save size={18} className="mr-2" /> {saving ? '...' : de ? 'Speichern' : 'Save'}
      </Button>
    </div>
  );
}
